/**
 * Persistence layer for wizard state
 * Handles both Supabase (authenticated) and localStorage (anonymous) storage
 */

import { supabase as supabaseClient } from '@/integrations/supabase/client';
import type { WizardState } from '@/stores/wizardStore';

// Type assertion to access full Supabase client methods
const supabase = supabaseClient as any;

function getAnonymousId(): string {
  let id = localStorage.getItem('wizard_anonymous_id');
  if (!id) {
    id = `anon_${crypto.randomUUID()}`;
    localStorage.setItem('wizard_anonymous_id', id);
  }
  return id;
}

function mapDbToState(dbRow: any): WizardState {
  return {
    id: dbRow.id,
    environment: dbRow.environment,
    personas: dbRow.personas,
    modelSelections: dbRow.model_selections,
    progress: dbRow.progress,
  };
}

export async function saveWizardState(
  state: WizardState
): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (user) {
    // Save to Supabase for authenticated users
    const { data, error } = await supabase
      .from('wizard_scenarios')
      .upsert({
        id: state.id || undefined,
        user_id: user.id,
        anonymous_id: null,
        environment: state.environment,
        personas: state.personas,
        model_selections: state.modelSelections,
        progress: state.progress,
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to save wizard state to Supabase:', error);
      throw error;
    }
    return data.id;
  } else {
    // Save to localStorage for anonymous users
    const anonymousId = getAnonymousId();
    const key = `wizard_scenario_${anonymousId}`;
    localStorage.setItem(key, JSON.stringify(state));
    return anonymousId;
  }
}

export async function loadWizardState(): Promise<WizardState | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    // Load from Supabase for authenticated users
    const { data, error } = await supabase
      .from('wizard_scenarios')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Failed to load wizard state from Supabase:', error);
      return null;
    }

    return data ? mapDbToState(data) : null;
  } else {
    // Load from localStorage for anonymous users
    const anonymousId = getAnonymousId();
    const key = `wizard_scenario_${anonymousId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  }
}

export async function clearWizardState(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    await supabase
      .from('wizard_scenarios')
      .delete()
      .eq('user_id', user.id);
  } else {
    const anonymousId = getAnonymousId();
    const key = `wizard_scenario_${anonymousId}`;
    localStorage.removeItem(key);
  }
}
